/**
 * Image URL utility functions for handling different image sources
 * - External URLs (https://images.unsplash.com, etc.) - return as-is
 * - Local API uploads (/api/uploads/...) - prefix with backend URL
 * - Relative paths - prefix with backend URL
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default placeholder for missing images
export const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=400&fit=crop&auto=format';

/**
 * Get the full URL for a product image
 * @param {string} imageUrl - The image URL from the database
 * @param {string} fallback - Optional fallback URL if imageUrl is empty
 * @returns {string} The full URL to use in img src
 */
export function getImageUrl(imageUrl, fallback = PLACEHOLDER_IMAGE) {
  // Handle null/undefined/empty
  if (!imageUrl) {
    return fallback;
  }

  // Already a full external URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Local API path - prefix with backend URL
  if (imageUrl.startsWith('/api/') || imageUrl.startsWith('/api')) {
    return `${BACKEND_URL}${imageUrl}`;
  }

  // Other relative paths - prefix with backend URL
  if (imageUrl.startsWith('/')) {
    return `${BACKEND_URL}${imageUrl}`;
  }

  // Assume it's a relative path without leading slash
  return `${BACKEND_URL}/${imageUrl}`;
}

/**
 * Get the first image from a product's images array with proper URL handling
 * @param {Object} product - The product object
 * @param {string} fallback - Optional fallback URL
 * @returns {string} The full URL for the product's primary image
 */
export function getProductImage(product, fallback = PLACEHOLDER_IMAGE) {
  const images = product?.images;
  
  if (!images || !Array.isArray(images) || images.length === 0) {
    return fallback;
  }

  return getImageUrl(images[0], fallback);
}

/**
 * Get all images from a product with proper URL handling
 * @param {Object} product - The product object
 * @returns {string[]} Array of full image URLs
 */
export function getAllProductImages(product) {
  const images = product?.images;
  
  if (!images || !Array.isArray(images) || images.length === 0) {
    return [PLACEHOLDER_IMAGE];
  }

  return images.map(img => getImageUrl(img, PLACEHOLDER_IMAGE));
}

export default {
  getImageUrl,
  getProductImage,
  getAllProductImages,
  PLACEHOLDER_IMAGE,
  handleImageError,
};

/**
 * Universal `<img onError>` handler that fixes the recurring "DB points
 * at /products/foo.png but the file on disk is /products/foo.jpg"
 * placeholder bug.
 *
 * On the first error, retry the SAME url stem with every other common
 * image extension before falling back to the configured placeholder.
 * Browsers cache 404s, so this only adds extra requests on the very
 * first miss — subsequent renders use the resolved URL from cache.
 *
 * Implementation note: we deliberately do NOT mutate img.src directly
 * (React reconciliation would clobber the new src on the next render).
 * Instead we set `img.src` via setAttribute and ALSO mirror it onto a
 * data attribute that React's diff treats as ours, then we let the
 * native browser image load run before React's next reconcile pass.
 *
 * Use like:
 *   <img src={getProductImage(p)} onError={handleImageError} />
 *
 * Why this exists: file extensions in the DB drift out of sync with
 * the actual files on disk every time we run a migration (e.g., the
 * Feb 2026 .png → .jpg cleanup of Nano Banana hero images). Instead of
 * keeping the DB and disk perfectly in lockstep — which is fragile —
 * we let the browser try the obvious siblings before declaring defeat.
 * That makes the bug class user-invisible.
 */
const _EXT_FALLBACKS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

export function handleImageError(e, fallback = PLACEHOLDER_IMAGE) {
  const img = e?.target;
  if (!img) return;

  // Pull the ORIGINAL URL we first attempted. React passes the same
  // `src` prop on every render, so we lock the first attempt into a
  // data attribute and derive the stem from there forever.
  const original = img.dataset.originalSrc || img.src;
  if (!img.dataset.originalSrc) img.dataset.originalSrc = original;

  const match = original.match(/^(.+)\.([a-z]+)(\?.*)?$/i);
  if (!match) {
    img.onerror = null;
    img.src = fallback;
    return;
  }
  const [, stem, originalExt, qs = ''] = match;

  // Track every extension we've tried via a data attribute so React's
  // reconciliation can't reset our retry progress.
  let tried;
  try {
    tried = JSON.parse(img.dataset.triedExts || '[]');
  } catch {
    tried = [];
  }
  if (!tried.includes(originalExt.toLowerCase())) {
    tried.push(originalExt.toLowerCase());
  }
  // Also record whatever extension we just failed (could differ if we
  // already swapped once).
  const currentSrcMatch = img.src.match(/^(.+)\.([a-z]+)(\?.*)?$/i);
  if (currentSrcMatch) {
    const currentExt = currentSrcMatch[2].toLowerCase();
    if (!tried.includes(currentExt)) tried.push(currentExt);
  }
  img.dataset.triedExts = JSON.stringify(tried);

  const next = _EXT_FALLBACKS.find((ext) => !tried.includes(ext));
  if (next) {
    img.src = `${stem}.${next}${qs}`;
    return;
  }

  // Exhausted siblings — fall back to the placeholder. Detach the
  // handler so we don't loop if the placeholder itself ever fails.
  img.onerror = null;
  img.src = fallback;
}
